import {
    Box,
    Typography,
    Divider,
    Grid,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    MenuItem,
    InputAdornment,
    Button,
} from '@mui/material';
import { useFormikContext } from 'formik';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaletteIcon from '@mui/icons-material/Palette';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { ThemeCustomization } from '../../../../modules/public-event/types';
import { useMemo } from 'react';
import React from 'react';
import { ColorPicker } from './ColorPicker';

function isValidHexColor(color: string): boolean {
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(color);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }

    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
}

function getLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((val) => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function calculateContrast(color1: string, color2: string): number {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);

    if (!rgb1 || !rgb2) {
        return 1;
    }

    const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
    const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + 0.05) / (darker + 0.05);
}

function checkThemeAccessibility(theme: ThemeCustomization | undefined, defaultColors: Record<string, string>) {
    const issues: string[] = [];
    const warnings: string[] = [];

    if (!theme?.colors) {
        return { hasIssues: false, issues: [], warnings: [] };
    }

    const colors = theme.colors;
    const bgColor = colors.backgroundColor || defaultColors.backgroundColor;
    const secondaryBgColor = colors.secondaryBackgroundColor || defaultColors.secondaryBackgroundColor;
    const textColor = colors.textColor || defaultColors.textColor;
    const textSecondaryColor = colors.textSecondaryColor || defaultColors.textSecondaryColor;
    const primaryColor = colors.primaryColor || defaultColors.primaryColor;

    if (textColor && bgColor) {
        const contrast = calculateContrast(textColor, bgColor);
        if (contrast < 4.5) {
            issues.push(`Contraste entre texto principal e fundo principal (${contrast.toFixed(2)}:1) não atende WCAG AA (mínimo 4.5:1)`);
        } else if (contrast < 7) {
            warnings.push(`Contraste entre texto principal e fundo principal (${contrast.toFixed(2)}:1) atende WCAG AA, mas não atende AAA (recomendado 7:1)`);
        }
    }

    if (textColor && secondaryBgColor) {
        const contrast = calculateContrast(textColor, secondaryBgColor);
        if (contrast < 4.5) {
            issues.push(`Contraste entre texto principal e fundo secundário (${contrast.toFixed(2)}:1) não atende WCAG AA (mínimo 4.5:1)`);
        } else if (contrast < 7) {
            warnings.push(`Contraste entre texto principal e fundo secundário (${contrast.toFixed(2)}:1) atende WCAG AA, mas não atende AAA (recomendado 7:1)`);
        }
    }

    if (textSecondaryColor && bgColor) {
        const contrast = calculateContrast(textSecondaryColor, bgColor);
        if (contrast < 4.5) {
            issues.push(`Contraste entre texto secundário e fundo principal (${contrast.toFixed(2)}:1) não atende WCAG AA (mínimo 4.5:1)`);
        }
    }

    if (textSecondaryColor && secondaryBgColor) {
        const contrast = calculateContrast(textSecondaryColor, secondaryBgColor);
        if (contrast < 4.5) {
            issues.push(`Contraste entre texto secundário e fundo secundário (${contrast.toFixed(2)}:1) não atende WCAG AA (mínimo 4.5:1)`);
        }
    }

    if (primaryColor && bgColor) {
        const contrast = calculateContrast(primaryColor, bgColor);
        if (contrast < 3) {
            issues.push(`Contraste entre cor primária e fundo (${contrast.toFixed(2)}:1) é muito baixo para elementos interativos (recomendado mínimo 3:1)`);
        }
    }

    return {
        hasIssues: issues.length > 0,
        issues,
        warnings,
    };
}

const COMMON_FONTS = [
    { value: 'Manrope, sans-serif', label: 'Manrope' },
    { value: 'Roboto, sans-serif', label: 'Roboto' },
    { value: 'Open Sans, sans-serif', label: 'Open Sans' },
    { value: 'Lato, sans-serif', label: 'Lato' },
    { value: 'Montserrat, sans-serif', label: 'Montserrat' },
    { value: 'Poppins, sans-serif', label: 'Poppins' },
];

function ColorPreview({ color, defaultColor }: { color?: string; defaultColor: string }) {
    const displayColor = color || defaultColor;
    return (
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: displayColor,
                flexShrink: 0,
            }}
        />
    );
}

export function ThemeCustomizationSection() {
    const { values, setFieldValue, errors, touched } = useFormikContext<any>();
    const theme = values.theme_customization as ThemeCustomization | undefined;

    const defaultColors = useMemo(() => ({
        backgroundColor: '#F5F5F5',
        secondaryBackgroundColor: '#F5F5F5',
        primaryColor: '#1565C0',
        textColor: '#222222',
        textSecondaryColor: '#424242',
        gradientStart: '#6A5ACD',
        gradientMiddle: '#695fac',
        gradientEnd: '#FF69B4',
    }), []);

    const handleColorChange = (path: string, value: string) => {
        const currentTheme = theme || {};
        const newTheme = {
            ...currentTheme,
            colors: {
                ...currentTheme.colors,
                [path]: value || undefined,
            },
        };

        if (!newTheme.colors || Object.keys(newTheme.colors).length === 0) {
            setFieldValue('theme_customization', undefined);
        } else {
            setFieldValue('theme_customization', newTheme);
        }
    };

    const handleGradientChange = (path: 'start' | 'middle' | 'end', value: string) => {
        const currentTheme = theme || {};
        const newTheme = {
            ...currentTheme,
            colors: {
                ...currentTheme.colors,
                gradient: {
                    ...currentTheme.colors?.gradient,
                    [path]: value || undefined,
                },
            },
        };

        setFieldValue('theme_customization', newTheme);
    };

    const handleTypographyChange = (path: string, value: string) => {
        const currentTheme = theme || {};
        const newTheme = {
            ...currentTheme,
            typography: {
                ...currentTheme.typography,
                [path]: value || undefined,
            },
        };

        setFieldValue('theme_customization', newTheme);
    };

    const accessibilityCheck = useMemo(() => {
        return checkThemeAccessibility(theme, defaultColors);
    }, [theme, defaultColors]);

    return (
        <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaletteIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        Personalização de Tema
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                    Personalize as cores e estilos do seu evento. As cores serão aplicadas na página pública do evento.
                    <br />
                    <strong>Dica:</strong> Use cores com bom contraste para garantir acessibilidade (WCAG AA).
                </Alert>

                {accessibilityCheck.hasIssues ? (
                    <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            ⚠️ Problemas de Acessibilidade Detectados
                        </Typography>
                        <Typography variant="body2" component="div">
                            As seguintes combinações de cores não atendem aos padrões WCAG AA (mínimo 4.5:1):
                            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                {accessibilityCheck.issues.map((issue, index) => (
                                    <li key={index}>{issue}</li>
                                ))}
                            </Box>
                        </Typography>
                    </Alert>
                ) : accessibilityCheck.warnings.length > 0 ? (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            ⚠️ Avisos de Acessibilidade
                        </Typography>
                        <Typography variant="body2" component="div">
                            Suas cores atendem WCAG AA, mas podem ser melhoradas para WCAG AAA:
                            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                {accessibilityCheck.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </Box>
                        </Typography>
                    </Alert>
                ) : theme?.colors ? (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            ✓ Cores em Conformidade
                        </Typography>
                        <Typography variant="body2">
                            Todas as combinações de cores atendem aos padrões de acessibilidade WCAG AA (mínimo 4.5:1).
                        </Typography>
                    </Alert>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setFieldValue('theme_customization', null)}
                        sx={{
                            textTransform: 'none',
                        }}
                    >
                        Restaurar Valores Padrão
                    </Button>
                </Box>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Cores Principais
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.backgroundColor}
                                defaultColor={defaultColors.backgroundColor}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Cor de Fundo Principal"
                                    type="color"
                                    value={theme?.colors?.backgroundColor || defaultColors.backgroundColor}
                                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        handleColorChange('backgroundColor', e.target.value);
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        opacity: 0,
                                        width: 0,
                                        height: 0,
                                        pointerEvents: 'none'
                                    }}
                                />
                                <TextField
                                    label="Cor de Fundo Principal"
                                    value={theme?.colors?.backgroundColor || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleColorChange('backgroundColor', value);
                                        }
                                    }}
                                    placeholder={defaultColors.backgroundColor}
                                    error={!!(touched.theme_customization && errors.theme_customization)}
                                    helperText={
                                        touched.theme_customization && errors.theme_customization
                                            ? 'Cor hexadecimal inválida'
                                            : 'Cor de fundo da página'
                                    }
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.backgroundColor}
                                                    defaultValue={defaultColors.backgroundColor}
                                                    onChange={(color) => handleColorChange('backgroundColor', color)}
                                                    label="Cor de Fundo Principal"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.secondaryBackgroundColor}
                                defaultColor={defaultColors.secondaryBackgroundColor}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Cor de Fundo Secundária"
                                    value={theme?.colors?.secondaryBackgroundColor || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleColorChange('secondaryBackgroundColor', value);
                                        }
                                    }}
                                    placeholder={defaultColors.secondaryBackgroundColor}
                                    helperText="Cor de fundo Secundária"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.secondaryBackgroundColor}
                                                    defaultValue={defaultColors.secondaryBackgroundColor}
                                                    onChange={(color) => handleColorChange('secondaryBackgroundColor', color)}
                                                    label="Cor de Fundo Secundária"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.primaryColor}
                                defaultColor={defaultColors.primaryColor}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Cor Primária"
                                    value={theme?.colors?.primaryColor || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleColorChange('primaryColor', value);
                                        }
                                    }}
                                    placeholder={defaultColors.primaryColor}
                                    helperText="Cor de botões, links e destaques"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.primaryColor}
                                                    defaultValue={defaultColors.primaryColor}
                                                    onChange={(color) => handleColorChange('primaryColor', color)}
                                                    label="Cor Primária"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.textColor}
                                defaultColor={defaultColors.textColor}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Cor do Texto Principal"
                                    value={theme?.colors?.textColor || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleColorChange('textColor', value);
                                        }
                                    }}
                                    placeholder={defaultColors.textColor}
                                    helperText="Cor do texto principal"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.textColor}
                                                    defaultValue={defaultColors.textColor}
                                                    onChange={(color) => handleColorChange('textColor', color)}
                                                    label="Cor do Texto Principal"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.textSecondaryColor}
                                defaultColor={defaultColors.textSecondaryColor}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Cor do Texto Secundário"
                                    value={theme?.colors?.textSecondaryColor || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleColorChange('textSecondaryColor', value);
                                        }
                                    }}
                                    placeholder={defaultColors.textSecondaryColor}
                                    helperText="Cor do texto secundário"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.textSecondaryColor}
                                                    defaultValue={defaultColors.textSecondaryColor}
                                                    onChange={(color) => handleColorChange('textSecondaryColor', color)}
                                                    label="Cor do Texto Secundário"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                    Gradiente do Header (Opcional)
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.gradient?.start}
                                defaultColor={defaultColors.gradientStart}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Gradiente Início"
                                    value={theme?.colors?.gradient?.start || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleGradientChange('start', value);
                                        }
                                    }}
                                    placeholder={defaultColors.gradientStart}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.gradient?.start}
                                                    defaultValue={defaultColors.gradientStart}
                                                    onChange={(color) => handleGradientChange('start', color)}
                                                    label="Gradiente Início"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.gradient?.middle}
                                defaultColor={defaultColors.gradientMiddle}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Gradiente Meio"
                                    value={theme?.colors?.gradient?.middle || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleGradientChange('middle', value);
                                        }
                                    }}
                                    placeholder={defaultColors.gradientMiddle}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.gradient?.middle}
                                                    defaultValue={defaultColors.gradientMiddle}
                                                    onChange={(color) => handleGradientChange('middle', color)}
                                                    label="Gradiente Meio"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ColorPreview
                                color={theme?.colors?.gradient?.end}
                                defaultColor={defaultColors.gradientEnd}
                            />
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Gradiente Fim"
                                    value={theme?.colors?.gradient?.end || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value || isValidHexColor(value)) {
                                            handleGradientChange('end', value);
                                        }
                                    }}
                                    placeholder={defaultColors.gradientEnd}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ColorPicker
                                                    value={theme?.colors?.gradient?.end}
                                                    defaultValue={defaultColors.gradientEnd}
                                                    onChange={(color) => handleGradientChange('end', color)}
                                                    label="Gradiente Fim"
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                    Tipografia (Opcional)
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label="Família de Fonte"
                            value={theme?.typography?.fontFamily || ''}
                            onChange={(e) => handleTypographyChange('fontFamily', e.target.value)}
                            fullWidth
                            helperText="Selecione uma fonte para o evento"
                        >
                            <MenuItem value="">
                                <em>Padrão (Manrope)</em>
                            </MenuItem>
                            {COMMON_FONTS.map((font) => (
                                <MenuItem key={font.value} value={font.value}>
                                    {font.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

